import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { comics, categories } from '@/mocks/comics';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function Category() {
  const { slug } = useParams<{ slug: string }>();
  const [activeCategory, setActiveCategory] = useState(slug || 'all');
  const [currentPage, setCurrentPage] = useState(1);
  const perPage = 12;

  useEffect(() => {
    if (slug) {
      setActiveCategory(slug);
      setCurrentPage(1);
    }
  }, [slug]);

  const filtered =
    activeCategory === 'all'
      ? comics
      : comics.filter((c) => c.category === activeCategory);

  const totalPages = Math.ceil(filtered.length / perPage);
  const paginated = filtered.slice(
    (currentPage - 1) * perPage,
    currentPage * perPage
  );

  const activeCategoryName =
    categories.find((c) => c.slug === activeCategory)?.name || '全部漫画';

  const webPageSchema = {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name: `${activeCategoryName} - 韩漫网`,
    description: `韩漫网${activeCategoryName}分类，浏览所有${activeCategoryName}类型的韩国漫画，每日更新。`,
    url: `https://www.hanmanwang.com/category/${activeCategory}`,
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(webPageSchema)}</script>

      <section className="bg-background-100/70">
        <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-8">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '分类浏览', path: '/category/all' },
              { label: activeCategoryName },
            ]}
          />
          <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mt-3">
            {activeCategoryName}
          </h1>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="flex flex-wrap items-center gap-2 mb-8">
          {categories.map((cat) => {
            const count =
              cat.slug === 'all'
                ? comics.length
                : comics.filter((c) => c.category === cat.slug).length;
            return (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                onClick={() => setActiveCategory(cat.slug)}
                className={`flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-label whitespace-nowrap transition-all duration-200 cursor-pointer ${
                  activeCategory === cat.slug
                    ? 'bg-primary-500 text-background-50'
                    : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                }`}
              >
                <i className={`${cat.icon} text-xs`}></i>
                {cat.name}
                <span
                  className={`text-[10px] ml-0.5 ${
                    activeCategory === cat.slug
                      ? 'text-background-50/70'
                      : 'text-foreground-400'
                  }`}
                >
                  ({count})
                </span>
              </Link>
            );
          })}
        </div>

        {filtered.length === 0 ? (
          <div className="text-center py-20">
            <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
              <i className="ri-emotion-sad-line text-3xl"></i>
            </div>
            <p className="text-foreground-500">该分类暂无漫画</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-4 md:gap-5">
              {paginated.map((comic) => (
                <ComicCard
                  key={comic.id}
                  {...comic}
                  categoryName={categories.find((c) => c.slug === comic.category)?.name}
                />
              ))}
            </div>

            {totalPages > 1 && (
              <div className="flex items-center justify-center gap-2 mt-12">
                <button
                  disabled={currentPage === 1}
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  className="px-4 py-2 rounded-full text-sm font-label bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                >
                  上一页
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setCurrentPage(p)}
                    className={`w-9 h-9 rounded-full text-sm font-label transition-colors whitespace-nowrap cursor-pointer ${
                      currentPage === p
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                    }`}
                  >
                    {p}
                  </button>
                ))}
                <button
                  disabled={currentPage === totalPages}
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  className="px-4 py-2 rounded-full text-sm font-label bg-background-100 text-foreground-600 hover:bg-background-200 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap cursor-pointer"
                >
                  下一页
                </button>
              </div>
            )}
          </>
        )}
      </section>
    </>
  );
}